/**
 * Camera and transient interaction state.
 *
 * Kept out of the plan store on purpose: the camera changes at pointer-move
 * frequency, and mixing it into the document would re-render every table on
 * every frame of a pan — and pollute undo history with camera moves.
 */

import { create } from 'zustand'

import { ROOM_BOUNDS, type Bounds } from '../lib/geometry'
import type { SeatRef } from './planStore'

export const MIN_ZOOM = 0.2
export const MAX_ZOOM = 3
/**
 * One pitch for the whole grid: the canvas draws its dot lattice at this
 * spacing and `snap()` rounds to it, so a snapped table always lands on a dot
 * the planner can see. (It used to snap to half the drawn pitch — tables
 * landed *between* dots and the snap button looked like it did nothing.)
 */
export const GRID_SIZE = 20

export interface Camera {
  x: number
  y: number
  zoom: number
}

/** A guest being dragged from the roster or from another chair. */
export interface DragPayload {
  guestId: string
  from: SeatRef | null
  pointer: { x: number; y: number }
}

/**
 * What the compact editor's sheet is showing. `selection` is the inspector for
 * whatever is selected — a table, several, or a chair.
 */
export type EditorPanel = 'tables' | 'guests' | 'plan' | 'selection'

/**
 * Something waiting for the planner's next tap on the room: a guest for a chair
 * or table, or a table's guests for the table they are to trade with. One
 * state, so a tap, Escape, the banner and the sheet treat both alike.
 */
export type Placement = (
  | { kind: 'guest'; guestId: string }
  | { kind: 'swap'; tableId: string }
) & {
  /** The panel the placement was started from, reopened once it is over. */
  returnTo: EditorPanel | null
}

interface ViewState extends Camera {
  snapToGrid: boolean
  showGrid: boolean
  isPanning: boolean
  drag: DragPayload | null
  hoverSeat: SeatRef | null
  /**
   * The editor is one canvas with a sheet over it rather than three columns
   * (a phone, a tablet upright, a narrow window). Mirrored here from the media
   * query so pointer handlers can ask without a React render.
   */
  compact: boolean
  panel: EditorPanel | null
  /** The sheet is pulled up to full height. */
  panelExpanded: boolean
  /** Taps add tables to the selection and a drag on the floor draws a box — Shift, for touch. */
  selectMode: boolean
  placing: Placement | null
  /** Two fingers are on the canvas; one-finger gestures wait until both have lifted. */
  pinching: boolean
  /** When the last pinch ended (`performance.now()`), so its lifting fingers are not read as taps. */
  pinchEndedAt: number
  /**
   * How far the camera may roam, in world units. The room, widened to take in
   * any grandfathered table sitting outside it — panning must never strand
   * something the planner can still see on the canvas.
   */
  roam: Bounds
  /** Size of the element the camera looks through, for clamping the pan. */
  viewportSize: { width: number; height: number }

  setRoam: (bounds: Bounds, viewportSize: { width: number; height: number }) => void
  setCamera: (camera: Partial<Camera>) => void
  panBy: (dx: number, dy: number) => void
  /** Zoom keeping the world point under the cursor pinned to the cursor. */
  zoomAt: (factor: number, screenX: number, screenY: number) => void
  zoomTo: (zoom: number, viewportSize: { width: number; height: number }) => void
  fitTo: (bounds: Bounds, viewportSize: { width: number; height: number }) => void
  centerOn: (world: { x: number; y: number }, viewportSize: { width: number; height: number }) => void
  resetCamera: (camera: Camera) => void

  toggleSnap: () => void
  toggleGrid: () => void
  setPanning: (value: boolean) => void
  startDrag: (payload: DragPayload) => void
  moveDrag: (pointer: { x: number; y: number }) => void
  endDrag: () => void
  setHoverSeat: (ref: SeatRef | null) => void

  setCompact: (compact: boolean) => void
  /** Opens a panel in the sheet. A placement in progress is abandoned, not resumed. */
  openPanel: (panel: EditorPanel) => void
  /** Closes the sheet; the next panel opens at half height again. */
  closePanel: () => void
  setPanelExpanded: (expanded: boolean) => void
  setSelectMode: (on: boolean) => void
  /** Clears the sheet out of the way while the planner picks a chair. */
  startPlacing: (guestId: string) => void
  /** Waits for the table whose guests trade places with this one's. */
  startSwap: (tableId: string) => void
  /** Ends a placement — done or cancelled — and brings back the panel it came from. */
  stopPlacing: () => void
  setPinching: (pinching: boolean) => void
}

/** Screen pixels left around the room when zoomed all the way out on a small screen. */
const FLOOR_PADDING = 16

/**
 * How far out the camera may zoom. `MIN_ZOOM` on any screen the room fits at
 * that zoom; lower on a phone, where 20% still shows barely a third of the
 * hall — always far enough to see the whole floor at once.
 */
export function minZoomFor(state: {
  roam: Bounds
  viewportSize: { width: number; height: number }
}): number {
  const { roam, viewportSize } = state
  const fits = Math.min(
    (viewportSize.width - FLOOR_PADDING * 2) / Math.max(roam.maxX - roam.minX, 1),
    (viewportSize.height - FLOOR_PADDING * 2) / Math.max(roam.maxY - roam.minY, 1),
  )
  return Math.max(0.04, Math.min(MIN_ZOOM, fits))
}

const clampZoom = (
  zoom: number,
  state: { roam: Bounds; viewportSize: { width: number; height: number } },
): number => Math.min(MAX_ZOOM, Math.max(minZoomFor(state), zoom))

/** Slack around the room, so its edge isn't welded to the edge of the screen. */
export const ROAM_MARGIN = 160

/**
 * Keep the roamable world in view.
 *
 * Screen x = world x * zoom + camera x, so pinning the world's left edge no
 * further right than the viewport's left edge (and vice versa) is two
 * inequalities on the camera offset. When the world is smaller than the
 * viewport the two cross over, and the right answer is to centre it rather than
 * to pick one edge.
 */
function clampCamera(state: {
  x: number
  y: number
  zoom: number
  roam: Bounds
  viewportSize: { width: number; height: number }
}): { x: number; y: number } {
  const { zoom, roam, viewportSize } = state
  const axis = (offset: number, min: number, max: number, extent: number): number => {
    const low = extent - (max + ROAM_MARGIN) * zoom
    const high = -(min - ROAM_MARGIN) * zoom
    return low > high ? (low + high) / 2 : Math.min(Math.max(offset, low), high)
  }
  return {
    x: axis(state.x, roam.minX, roam.maxX, viewportSize.width),
    y: axis(state.y, roam.minY, roam.maxY, viewportSize.height),
  }
}

export const useViewStore = create<ViewState>()((set, get) => ({
  x: 0,
  y: 0,
  zoom: 1,
  snapToGrid: true,
  showGrid: true,
  isPanning: false,
  drag: null,
  hoverSeat: null,
  compact: false,
  panel: null,
  panelExpanded: false,
  selectMode: false,
  placing: null,
  pinching: false,
  pinchEndedAt: -Infinity,
  roam: ROOM_BOUNDS,
  viewportSize: { width: 1200, height: 800 },

  setRoam: (roam, viewportSize) =>
    set((state) => ({ roam, viewportSize, ...clampCamera({ ...state, roam, viewportSize }) })),

  setCamera: (camera) =>
    set((state) => clampCamera({ ...state, ...camera })),

  panBy: (dx, dy) =>
    set((state) => clampCamera({ ...state, x: state.x + dx, y: state.y + dy })),

  zoomAt: (factor, screenX, screenY) => {
    const state = get()
    const next = clampZoom(state.zoom * factor, state)
    if (next === state.zoom) return
    // World point under the cursor must not move: solve for the new offset.
    const worldX = (screenX - state.x) / state.zoom
    const worldY = (screenY - state.y) / state.zoom
    set({
      zoom: next,
      ...clampCamera({
        ...state,
        zoom: next,
        x: screenX - worldX * next,
        y: screenY - worldY * next,
      }),
    })
  },

  zoomTo: (zoom, size) => {
    get().zoomAt(clampZoom(zoom, get()) / get().zoom, size.width / 2, size.height / 2)
  },

  fitTo: (bounds, size) => {
    const padding = 96
    const width = Math.max(bounds.maxX - bounds.minX, 1)
    const height = Math.max(bounds.maxY - bounds.minY, 1)
    const zoom = clampZoom(
      Math.min((size.width - padding * 2) / width, (size.height - padding * 2) / height, 1.5),
      { roam: get().roam, viewportSize: size },
    )
    set((state) => ({
      zoom,
      viewportSize: size,
      ...clampCamera({
        ...state,
        zoom,
        viewportSize: size,
        x: size.width / 2 - ((bounds.minX + bounds.maxX) / 2) * zoom,
        y: size.height / 2 - ((bounds.minY + bounds.maxY) / 2) * zoom,
      }),
    }))
  },

  centerOn: (world, size) => {
    set((state) => ({
      viewportSize: size,
      ...clampCamera({
        ...state,
        viewportSize: size,
        x: size.width / 2 - world.x * state.zoom,
        y: size.height / 2 - world.y * state.zoom,
      }),
    }))
  },

  resetCamera: (camera) =>
    set((state) => {
      const zoom = clampZoom(camera.zoom, state)
      return { zoom, ...clampCamera({ ...state, ...camera, zoom }) }
    }),

  toggleSnap: () => set((state) => ({ snapToGrid: !state.snapToGrid })),
  toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
  setPanning: (value) => set({ isPanning: value }),

  // A drag is the other way to seat someone; the two never run at once.
  startDrag: (payload) => set({ drag: payload, placing: null }),
  moveDrag: (pointer) => set((state) => (state.drag ? { drag: { ...state.drag, pointer } } : {})),
  endDrag: () => set({ drag: null, hoverSeat: null }),
  setHoverSeat: (ref) => set({ hoverSeat: ref }),

  setCompact: (compact) => set({ compact }),
  openPanel: (panel) => set({ panel, placing: null }),
  closePanel: () => set({ panel: null, panelExpanded: false }),
  setPanelExpanded: (expanded) => set({ panelExpanded: expanded }),
  setSelectMode: (on) => set({ selectMode: on }),
  startPlacing: (guestId) =>
    set((state) => ({ placing: { kind: 'guest', guestId, returnTo: state.panel }, panel: null })),
  startSwap: (tableId) =>
    set((state) => ({ placing: { kind: 'swap', tableId, returnTo: state.panel }, panel: null })),
  stopPlacing: () =>
    set((state) =>
      state.placing ? { placing: null, panel: state.placing.returnTo ?? state.panel } : {},
    ),
  setPinching: (pinching) =>
    set(pinching ? { pinching } : { pinching, pinchEndedAt: performance.now() }),
}))

/** A pinch's fingers lift one at a time, and the browser still calls each lift a click. */
const TAP_AFTER_PINCH_MS = 350

/** Whether a tap now is really the tail of a two-finger gesture, and should do nothing. */
export function tapsSuppressed(state: { pinching: boolean; pinchEndedAt: number }): boolean {
  return state.pinching || performance.now() - state.pinchEndedAt < TAP_AFTER_PINCH_MS
}

/**
 * The camera that brings a world box inside the viewport, each edge kept its
 * own margin in screen pixels, by the shortest pan — or the same camera when
 * the box is already there. A box too big for the space is centred in it. A
 * sheet sliding up over the table the planner just tapped is what this undoes,
 * so it moves as little as it can.
 */
export function revealCamera(
  camera: Camera,
  box: Bounds,
  viewportSize: { width: number; height: number },
  margins: { top: number; right: number; bottom: number; left: number },
): Camera {
  const shift = (low: number, high: number, start: number, end: number): number => {
    if (high - low > end - start) return (start + end) / 2 - (low + high) / 2
    if (low < start) return start - low
    if (high > end) return end - high
    return 0
  }
  const { zoom } = camera
  return {
    x:
      camera.x +
      shift(
        box.minX * zoom + camera.x,
        box.maxX * zoom + camera.x,
        margins.left,
        viewportSize.width - margins.right,
      ),
    y:
      camera.y +
      shift(
        box.minY * zoom + camera.y,
        box.maxY * zoom + camera.y,
        margins.top,
        viewportSize.height - margins.bottom,
      ),
    zoom,
  }
}

/** Screen (container-relative) point -> world coordinates. */
export function screenToWorld(camera: Camera, screenX: number, screenY: number) {
  return { x: (screenX - camera.x) / camera.zoom, y: (screenY - camera.y) / camera.zoom }
}

export function snap(value: number, enabled: boolean): number {
  return enabled ? Math.round(value / GRID_SIZE) * GRID_SIZE : value
}
